import * as React from 'react';
import type { InjectedRouter } from 'react-router';

import { StatsigProvider } from '@coursera-web/statsig';
import { compose } from 'recompose';

import Retracked from 'js/app/retracked';
import connectToStores from 'js/lib/connectToStores';

import DefaultHeader from 'bundles/composable-page-header-consumer/header-renderers/DefaultHeader';
import UniversalPageFeatures from 'bundles/composable-page-header/components/UniversalPageFeatures';
import PageFooter from 'bundles/page-footer/components/PageFooter';
import ApplicationStoreClass from 'bundles/ssr/stores/ApplicationStore';
import InteractionGuard from 'bundles/unified-common/components/InteractionGuard';
import GlobalCoachChat from 'bundles/unified-common/components/coach-on-discovery/GlobalCoachChat';
import {
  GlobalCoachChatStateProvider,
  useGlobalCoachChatState,
} from 'bundles/unified-common/providers/GlobalCoachChatStateProvider';
import NavAiSparkleExperimentProvider, {
  useNavAiSparkle,
} from 'bundles/unified-common/providers/NavAiSparkleExperimentProvider';

import 'css!../styl/index';

type Context = {
  router: InjectedRouter;
  getStore: (storeName: string) => void;
};

type Props = {
  children?: React.ReactNode;
  requestCountryCode?: string;
  csrfToken?: string;
} & Context;

const ProfessionalCertificateCategoryPages = ({ children }: Props) => {
  const { showNavAiSparkle } = useNavAiSparkle();
  const { coachOpen, pageChatType } = useGlobalCoachChatState();
  const isFullscreenCoachChatOpen = coachOpen && pageChatType === null;

  return (
    <div className="rc-PremiumHubApp">
      <DefaultHeader isPositionStatic />
      <UniversalPageFeatures />
      {showNavAiSparkle && <GlobalCoachChat />}
      <InteractionGuard preventInteraction={isFullscreenCoachChatOpen}>
        {children}
        <PageFooter />
      </InteractionGuard>
    </div>
  );
};

const ProfessionalCertificateCategoryPagesWithProvider = ({ children, ...props }: Props) => {
  return (
    <StatsigProvider>
      <GlobalCoachChatStateProvider>
        <NavAiSparkleExperimentProvider>
          <ProfessionalCertificateCategoryPages {...props}>{children}</ProfessionalCertificateCategoryPages>
        </NavAiSparkleExperimentProvider>
      </GlobalCoachChatStateProvider>
    </StatsigProvider>
  );
};

export default compose<Props, {}>(
  Retracked.createTrackedContainer(() => ({
    namespace: {
      app: 'program_hub',
      page: 'professional_certificates_hub_app',
    },
  })),
  connectToStores<Props, Props & { csrfToken: string; requestCountryCode: string }>(
    [ApplicationStoreClass],
    (ApplicationStore, props) => ({
      ...props,
      csrfToken: ApplicationStore.getState().csrfToken,
      requestCountryCode: ApplicationStore.getState().requestCountryCode,
    })
  )
)(ProfessionalCertificateCategoryPagesWithProvider);
